import { Header } from '@components/Header';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { hasVsDeveloperRole } from '@helpers/hasVsDeveloperRole';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminOrganizationsListOrganizationDto, FeatureFlagsService, OrganizationsService } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useExternallyManagedFilter } from 'app/hooks/useExternallyManagedFilter';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { createFetchOrganizationsQueryFn } from 'app/queries/organizations/createFetchOrganizationsQueryFn';
import { organizationsKeys, TOrganizationsListParams } from 'app/queries/organizations/organizationsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { fetchAllOrganizationsForExport } from '../../api/fetchAllOrganizationsForExport';
import { downloadOrganizationsCsv } from '../../helpers/downloadOrganizationsCsv';
import { Table } from './components/Table';
import { Wrapper } from './styles/Wrapper';

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { me } = useAuthContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const [editId, setEditId] = useState('');
  const { includeExternallyManaged, toggleIncludeExternallyManaged } = useExternallyManagedFilter({ setPage });
  const isVsDeveloper = hasVsDeveloperRole(me?.roles);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TOrganizationsListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch, includeExternallyManaged }),
    [debouncedSearch, includeExternallyManaged, orderBy, orderType, page, pageSize],
  );

  const organizationsQuery = useQuery({
    queryKey: organizationsKeys.list(listParams),
    queryFn: createFetchOrganizationsQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  const refreshOrganizations = () => {
    closeModal();
    void queryClient.invalidateQueries({ queryKey: organizationsKeys.lists() });
  };

  const editFeatureFlagMutation = useMutation({
    mutationFn: ({ oid, name, value }: { oid: string; name: string; value: boolean }) =>
      call(() => FeatureFlagsService.updateOrganizationFeatureFlag({ oid, requestBody: { name, value } })),
    onSettled: refreshOrganizations,
  });

  const removeFeatureFlagMutation = useMutation({
    mutationFn: ({ oid, name }: { oid: string; name: string }) =>
      call(() => OrganizationsService.adminRemoveOrgFeatureFlag({ oid, ffname: name })),
    onSettled: refreshOrganizations,
  });

  const exportMutation = useMutation({
    mutationFn: () => fetchAllOrganizationsForExport(call, search, includeExternallyManaged),
    onSuccess: ({ data }) => {
      void downloadOrganizationsCsv(data);
    },
  });

  const deleteOrganizationMutation = useMutation({
    mutationFn: (organizationId: string) =>
      call(() => OrganizationsService.remove({ oid: organizationId }), {
        onError: (message) => toast.error(message || 'Unable to delete organization'),
      }),
    onSuccess: () => {
      toast.success('Organization marked as deleted');
      if (page > 1 && organizationsQuery.data?.data.length === 1) {
        setPage(page - 1);
      }
      refreshOrganizations();
    },
  });

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  const openAIModelChoiceModal = (
    e: React.MouseEvent<HTMLButtonElement>,
    organization: AdminOrganizationsListOrganizationDto,
  ) => {
    e.stopPropagation();
    openModal(ModalNamesEnum.AdminAIModelChoice, {
      organization,
      onSuccess: refreshOrganizations,
    });
  };

  const editOrganizationFeatureFlag = (oid: string, name: string, value: boolean) => {
    editFeatureFlagMutation.mutate({ oid, name, value });
  };

  const removeOrganizationFeatureFlag = async (oid: string, name: string) => {
    await removeFeatureFlagMutation.mutateAsync({ oid, name });
  };

  const openDeleteOrganizationModal = (organization: AdminOrganizationsListOrganizationDto) => {
    openModal(ModalNamesEnum.CustomConfirmationModal, {
      title: 'Delete organization',
      description: `This will mark ${organization.name} as deleted.`,
      buttonText: 'Delete',
      onCancel: closeModal,
      onSuccess: () => {
        closeModal();
        deleteOrganizationMutation.mutate(organization.id);
      },
    });
  };

  const organizations = organizationsQuery.data ?? { data: [], count: 0, featureFlags: {} };
  const isLoading =
    organizationsQuery.isFetching ||
    isSearchSettling ||
    editFeatureFlagMutation.isPending ||
    removeFeatureFlagMutation.isPending ||
    deleteOrganizationMutation.isPending;

  return (
    <>
      <Header
        title={'Organizations'}
        buttonLabel={organizations.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={organizations.data?.length ? () => exportCSV() : undefined}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        {isVsDeveloper ? (
          <div className="flex justify-end px-4 py-2">
            <Switch
              checked={includeExternallyManaged}
              onChange={toggleIncludeExternallyManaged}
              testId="show-externally-managed-switch"
              label="Show externally managed"
            />
          </div>
        ) : null}
        <Table
          organizations={organizations}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          editId={editId}
          setEditId={setEditId}
          onOpenAIModelChoiceModal={openAIModelChoiceModal}
          onEditFeatureFlag={editOrganizationFeatureFlag}
          onRemoveFeatureFlag={removeOrganizationFeatureFlag}
          canDeleteOrganizations={isVsDeveloper}
          isDeletingOrganization={deleteOrganizationMutation.isPending || organizationsQuery.isFetching}
          onDeleteOrganization={openDeleteOrganizationModal}
        />
      </Wrapper>
    </>
  );
};
